import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, User, Mail, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { api, mediaUrl, youtubeId, errMsg } from "@/lib/api";
import TourCard from "@/components/site/TourCard";
import RichText from "@/components/site/RichText";
import Reveal from "@/components/site/Reveal";
import Reviews from "@/components/site/Reviews";

function Hero({ s }) {
  const yt = youtubeId(s.hero_video_url || "");
  return (
    <section className="relative h-[78vh] min-h-[520px] overflow-hidden bg-gray-800" data-testid="hero-section">
      {s.hero_poster_url && <img src={mediaUrl(s.hero_poster_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {yt ? (
        <iframe
          title="hero-video"
          className="yt-bg"
          src={`https://www.youtube.com/embed/${yt}?autoplay=1&mute=1&loop=1&playlist=${yt}&controls=0&showinfo=0&modestbranding=1&playsinline=1&rel=0`}
          allow="autoplay; encrypted-media"
          frameBorder="0"
          data-testid="hero-youtube"
        />
      ) : s.hero_video_url ? (
        <video key={s.hero_video_url} className="absolute inset-0 w-full h-full object-cover" src={mediaUrl(s.hero_video_url)}
          autoPlay muted loop playsInline poster={mediaUrl(s.hero_poster_url)} data-testid="hero-video" />
      ) : null}
      <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${(s.hero_overlay ?? 45) / 100})` }} />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <h1 className="fade-up text-white font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-[54px] leading-[1.05] max-w-4xl" data-testid="hero-title">
          {s.hero_title}
        </h1>
        <p className="fade-up text-white/95 text-lg sm:text-xl mt-5" style={{ animationDelay: "0.15s" }}>{s.hero_subtitle}</p>
        <Link to="/#excursiones" data-testid="hero-cta-btn" style={{ animationDelay: "0.3s" }}
          className="fade-up btn-accent mt-8 font-semibold px-7 py-3 rounded-md shadow-lg">
          {s.hero_button}
        </Link>
      </div>
    </section>
  );
}

function Contact({ s, t }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Mensaje enviado! Te responderemos pronto.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(errMsg(err, "No se pudo enviar el mensaje"));
    } finally { setSending(false); }
  };
  const inputCls = "w-full h-12 rounded-md border border-gray-300 px-4 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition-[border-color,box-shadow]";
  return (
    <section id="contacto" className="py-16 sm:py-20 bg-white" data-testid="contact-section">
      <Reveal className="max-w-[560px] mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{s.contact_title}</h2>
          <p className="text-gray-500 mt-3">{s.contact_subtitle}</p>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"><User className="w-4 h-4" />{t("yourName")}</label>
            <input required data-testid="contact-name-input" className={inputCls} placeholder={t("namePh")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"><Mail className="w-4 h-4" />{t("yourEmail")}</label>
            <input required type="email" data-testid="contact-email-input" className={inputCls} placeholder={t("emailPh")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"><MessageSquare className="w-4 h-4" />{t("yourMessage")}</label>
            <textarea required rows={5} data-testid="contact-message-input" className={`${inputCls} h-auto py-3 resize-none`} placeholder={t("messagePh")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button disabled={sending} data-testid="contact-submit-btn" className="btn-brand w-full h-12 rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{t("sendMessage")}
          </button>
        </form>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const { settings: s, tours, destinations, t, loading } = useSite();
  const navigate = useNavigate();
  if (loading || !s) {
    return <div className="h-[70vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin brand-text" /></div>;
  }
  return (
    <div data-testid="home-page">
      <Hero s={s} />

      <section className="py-16 sm:py-20 bg-white">
        <Reveal className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold brand-text uppercase">{s.about_title}</h2>
          <p className="text-gray-600 text-lg leading-relaxed mt-6"><RichText text={s.about_text} /></p>
        </Reveal>
      </section>

      <section id="excursiones" className="py-16 bg-gray-50" data-testid="tours-section">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{s.tours_title}</h2>
            <p className="text-gray-500 mt-3">{s.tours_subtitle}</p>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {tours.map((tour) => <TourCard key={tour.id} tour={tour} />)}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="destinations-section">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{s.destinations_title}</h2>
            <p className="text-gray-500 mt-3">{s.destinations_subtitle}</p>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-6">
            {destinations.map((d, i) => (
              <button key={d.id} onClick={() => navigate(`/excursiones?destino=${encodeURIComponent(d.name)}`)} data-testid={`destination-card-${i}`}
                className={`tour-card group relative rounded-lg overflow-hidden text-left ${i < 2 ? "lg:col-span-3 h-[320px]" : "lg:col-span-2 h-[260px]"}`}>
                <img src={mediaUrl(d.image)} alt={d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="text-2xl font-bold text-white">{d.name}</h3>
                  <p className="text-white/85 text-sm">{d.tour_count} {t("toursCount")}</p>
                  <span className="inline-block mt-3 text-sm text-white font-medium border-b-2 border-[var(--accent-c)] pb-0.5 group-hover:pr-3 transition-[padding] duration-300">{t("viewAll")} →</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Reviews title={s.testimonials_title} subtitle={s.testimonials_subtitle} />

      <Contact s={s} t={t} />
    </div>
  );
}
